// API utility for email management
const API_BASE = import.meta.env.DEV 
  ? '/api/email' 
  : 'https://drunk-email-api.b-9f2.workers.dev/api/email';

export interface Domain {
  id: number;
  domain: string;
  default_email: string;
  is_catch_all: boolean;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmailEntry {
  id: number;
  email: string;
  url: string;
  domain_id?: number;
  domain_name?: string;
  is_catch_all?: boolean;
  description?: string;
  tags: string[];
  usage_count: number;
  last_used_at?: string;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplate {
  id: number;
  name: string;
  pattern: string;
  description?: string;
  user_id: number;
  is_global: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface Analytics {
  summary: {
    total_domains: number;
    total_emails: number;
    catch_all_domains: number;
  };
  usage_stats: Array<{
    action: string;
    count: number;
    date: string;
  }>;
  top_domains: Array<{
    domain: string;
    email_count: number;
    is_catch_all: boolean;
  }>;
  recent_activity: Array<{
    action: string;
    created_at: string;
    email: string;
    url: string;
  }>;
}

class EmailApiClient {
  private userId: number = 1; // Default user ID for now

  // Domains
  async getDomains(): Promise<Domain[]> {
    const response = await fetch(`${API_BASE}/domains?user_id=${this.userId}`);
    const data = await response.json();
    return data.domains || [];
  }

  async createDomain(domain: Omit<Domain, 'id' | 'created_at' | 'updated_at' | 'is_active'>): Promise<{ id: number }> {
    const response = await fetch(`${API_BASE}/domains`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...domain, user_id: this.userId })
    });
    return await response.json();
  }

  async deleteDomain(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/domains/${id}?user_id=${this.userId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete domain');
  }

  // Email Entries
  async getEmailEntries(filters?: { domain?: string; limit?: number }): Promise<EmailEntry[]> {
    const params = new URLSearchParams({ user_id: this.userId.toString() });
    if (filters?.domain) params.append('domain', filters.domain);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    
    const response = await fetch(`${API_BASE}/entries?${params}`);
    const data = await response.json();
    return data.entries || [];
  }

  async createEmailEntry(entry: Omit<EmailEntry, 'id' | 'created_at' | 'updated_at' | 'usage_count' | 'is_favorite'>): Promise<{ id: number }> {
    const response = await fetch(`${API_BASE}/entries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...entry, user_id: this.userId })
    });
    return await response.json();
  }

  async updateEmailEntry(id: number, updates: Partial<EmailEntry>): Promise<void> {
    const response = await fetch(`${API_BASE}/entries/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...updates, user_id: this.userId })
    });
    if (!response.ok) throw new Error('Failed to update email entry');
  }

  async deleteEmailEntry(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/entries/${id}?user_id=${this.userId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete email entry');
  }

  async copyEmail(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/entries/${id}/copy?user_id=${this.userId}`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to log email copy');
  }

  // Templates
  async getTemplates(): Promise<EmailTemplate[]> {
    const response = await fetch(`${API_BASE}/templates?user_id=${this.userId}`);
    const data = await response.json();
    return data.templates || [];
  }

  async generateEmail(templateId: number, domainId: number, variables?: Record<string, string>): Promise<{
    email: string;
    template: EmailTemplate;
    domain: Domain;
  }> {
    const response = await fetch(`${API_BASE}/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        template_id: templateId,
        domain_id: domainId,
        variables: variables || {},
        user_id: this.userId
      })
    });
    return await response.json();
  }

  // Analytics
  async getAnalytics(days: number = 30): Promise<Analytics> {
    const response = await fetch(`${API_BASE}/analytics?user_id=${this.userId}&days=${days}`);
    return await response.json();
  }

  // Bulk operations
  async bulkDeleteEmails(emailIds: number[]): Promise<{ deleted_count: number }> {
    const response = await fetch(`${API_BASE}/bulk-delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email_ids: emailIds, user_id: this.userId })
    });
    return await response.json();
  }

  // Utility methods
  setUserId(userId: number) {
    this.userId = userId;
  }

  getUserId(): number {
    return this.userId;
  }
}

export const emailApi = new EmailApiClient();